import { useEffect, useMemo, useRef, useState } from "react";
import { useForm, type FieldErrors } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  Banknote,
  Building2,
  ChevronRight,
  FileText,
  Landmark,
  Loader2,
  MapPin,
  ShieldCheck,
  Truck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input, NativeSelect } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field, FieldGrid, FormSection, ToggleField } from "@/components/shared/form";
import { ErrorBoundary } from "@/components/shared/error-boundary";
import {
  clientToForm,
  useClient,
  useCreateClient,
  useNextClientCode,
  useUpdateClient,
} from "@/features/clients/api";
import {
  clientFormSchema,
  emptyClientForm,
  type ClientFormValues,
} from "@/features/clients/schema";
import {
  CLIENT_TYPES,
  DELIVERY_TERMS,
  FREIGHT_TERMS,
  GST_TREATMENTS,
  INDIAN_STATES,
  INDUSTRY_SEGMENTS,
  PACKING_TYPES,
  PAYMENT_TERMS,
  PRICE_LISTS,
  QUALITY_STANDARDS,
  RATE_BASIS,
} from "@/features/clients/constants";
import { cn } from "@/lib/utils";

const SECTIONS = [
  { id: "identity", label: "Identity", icon: Building2 },
  { id: "reach", label: "Contact", icon: FileText },
  { id: "address", label: "Address", icon: MapPin },
  { id: "statutory", label: "Tax & statutory", icon: Landmark },
  { id: "commercial", label: "Commercial terms", icon: Banknote },
  { id: "dispatch", label: "Dispatch", icon: Truck },
  { id: "quality", label: "Quality", icon: ShieldCheck },
];

export function ClientNewPage() {
  return (
    <ErrorBoundary title="Client form crashed">
      <ClientFormPage mode="create" />
    </ErrorBoundary>
  );
}

export function ClientEditPage({ clientId }: { clientId: string }) {
  return (
    <ErrorBoundary title="Client form crashed">
      <ClientFormPage mode="edit" clientId={clientId} />
    </ErrorBoundary>
  );
}

function ClientFormPage({ mode, clientId }: { mode: "create" | "edit"; clientId?: string }) {
  const navigate = useNavigate();
  const isEdit = mode === "edit";
  const { data: client, isLoading } = useClient(isEdit ? clientId : undefined);
  const { data: nextCode } = useNextClientCode(!isEdit);
  const createClient = useCreateClient();
  const updateClient = useUpdateClient();
  const [activeSection, setActiveSection] = useState("identity");
  const formRef = useRef<HTMLFormElement>(null);

  const form = useForm<ClientFormValues>({
    resolver: zodResolver(clientFormSchema),
    defaultValues: emptyClientForm,
    mode: "onBlur",
  });

  const { errors, isDirty } = form.formState;

  useEffect(() => {
    if (isEdit && client) form.reset(clientToForm(client));
  }, [isEdit, client, form]);

  useEffect(() => {
    if (!isEdit && nextCode && !form.getValues("code")) {
      form.setValue("code", nextCode);
    }
  }, [isEdit, nextCode, form]);

  // Scroll-spy so the rail always reflects where the user is in a long form.
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible) setActiveSection(visible.target.id);
      },
      { rootMargin: "-96px 0px -60% 0px", threshold: 0 },
    );
    SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [isLoading]);

  const submit = form.handleSubmit(async (values) => {
    if (isEdit && client) {
      await updateClient.mutateAsync({ id: client.id, values });
      void navigate({ to: "/clients/$clientId/profile", params: { clientId: client.id } });
    } else {
      const created = await createClient.mutateAsync(values);
      if (created?.id) {
        void navigate({ to: "/clients/$clientId/contacts", params: { clientId: created.id } });
      }
    }
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        void submit();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [submit]);

  const errorCount = useMemo(() => countErrors(errors), [errors]);
  const busy = createClient.isPending || updateClient.isPending;
  const gstTreatment = form.watch("gst_treatment");
  const gstRequired = gstTreatment === "registered" || gstTreatment === "composition";
  const paymentTerms = form.watch("payment_terms");

  function cancel() {
    if (isEdit && clientId) {
      void navigate({ to: "/clients/$clientId/profile", params: { clientId } });
    } else {
      void navigate({ to: "/clients" });
    }
  }

  if (isEdit && isLoading) {
    return <div className="py-16 text-center text-sm text-muted-foreground">Loading client…</div>;
  }

  return (
    <div className="pb-20">
      <div className="sticky top-0 z-20 -mx-4 mb-4 border-b border-border bg-background/85 px-4 py-2.5 backdrop-blur-md md:-mx-6 md:px-6">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <nav className="flex items-center gap-1 text-2xs text-muted-foreground">
              <Link to="/clients" className="hover:text-foreground">
                Clients
              </Link>
              <ChevronRight className="size-3" />
              <span className="text-foreground">{isEdit ? client?.code : "New"}</span>
            </nav>
            <h1 className="truncate text-base font-semibold tracking-[-0.01em]">
              {isEdit ? client?.name : "New client"}
            </h1>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {errorCount > 0 ? (
              <span className="text-2xs font-medium text-destructive">
                {errorCount} field{errorCount > 1 ? "s" : ""} need attention
              </span>
            ) : null}
            <Button type="button" variant="ghost" size="sm" onClick={cancel} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" form="client-form" size="sm" disabled={busy || (isEdit && !isDirty)}>
              {busy ? <Loader2 className="animate-spin" /> : null}
              {isEdit ? "Save changes" : "Create client"}
            </Button>
          </div>
        </div>
      </div>

      <div className="flex gap-6">
        <aside className="hidden w-40 shrink-0 lg:block">
          <nav className="sticky top-24 space-y-px">
            {SECTIONS.map((section) => {
              const Icon = section.icon;
              return (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  className={cn(
                    "flex items-center gap-2 rounded-md px-2 py-1.5 text-xs transition-colors",
                    activeSection === section.id
                      ? "bg-accent font-medium text-accent-foreground"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground",
                  )}
                >
                  <Icon className="size-3.5" />
                  {section.label}
                </a>
              );
            })}
          </nav>
        </aside>

        <form
          id="client-form"
          ref={formRef}
          onSubmit={submit}
          className="min-w-0 flex-1 space-y-4"
          noValidate
        >
          <FormSection
            id="identity"
            title="Identity & classification"
            description="How this account is grouped for pricing and reporting"
            icon={<Building2 />}
          >
            <FieldGrid>
              <Field label="Company name" required span={6} error={errors.name?.message} htmlFor="name">
                <Input id="name" {...form.register("name")} placeholder="Acme Steel Tubes Pvt Ltd" autoFocus />
              </Field>
              <Field label="Client code" required span={3} error={errors.code?.message} htmlFor="code">
                <Input id="code" {...form.register("code")} className="font-mono" />
              </Field>
              <Field label="Onboarded on" span={3} htmlFor="onboarded_on">
                <Input id="onboarded_on" type="date" {...form.register("onboarded_on")} />
              </Field>

              <Field label="Client type" required span={4} htmlFor="client_type">
                <NativeSelect id="client_type" {...form.register("client_type")}>
                  <option value="" disabled>
                    Select type
                  </option>
                  {CLIENT_TYPES.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Field label="Industry segment" span={4} htmlFor="industry_segment">
                <NativeSelect id="industry_segment" {...form.register("industry_segment")}>
                  <option value="">Select segment</option>
                  {INDUSTRY_SEGMENTS.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Field label="Lead source" span={4} htmlFor="lead_source">
                <Input id="lead_source" {...form.register("lead_source")} placeholder="Referral, exhibition…" />
              </Field>

              <Field label="Tags" span={8} hint="Comma separated — used for filters and price groups" htmlFor="groups">
                <Input id="groups" {...form.register("groups")} placeholder="Key account, North zone" />
              </Field>
              <ToggleField
                label="Active account"
                description="Inactive clients are hidden from order entry"
                checked={form.watch("is_active")}
                onChange={(v) => form.setValue("is_active", v, { shouldDirty: true })}
              />
            </FieldGrid>
          </FormSection>

          <FormSection
            id="reach"
            title="Company contact"
            description="Board line and official email — people go under Contacts"
            icon={<FileText />}
          >
            <FieldGrid>
              <Field label="Work phone" span={4} error={errors.work_phone?.message} htmlFor="work_phone">
                <Input id="work_phone" {...form.register("work_phone")} placeholder="+91 22 4000 0000" />
              </Field>
              <Field label="Email" span={4} error={errors.email?.message} htmlFor="email">
                <Input id="email" type="email" {...form.register("email")} placeholder="accounts@acme.co.in" />
              </Field>
              <Field label="Website" span={4} htmlFor="website">
                <Input id="website" {...form.register("website")} placeholder="acmesteel.co.in" />
              </Field>
              <ToggleField
                label="Print primary contact on documents"
                description="Adds the primary contact to invoices and delivery challans"
                span={12}
                checked={form.watch("show_primary_contact_on_docs")}
                onChange={(v) => form.setValue("show_primary_contact_on_docs", v, { shouldDirty: true })}
              />
            </FieldGrid>
          </FormSection>

          <FormSection
            id="address"
            title="Registered address"
            description="Billing and shipping locations are managed separately in the hub"
            icon={<MapPin />}
          >
            <FieldGrid>
              <Field label="Address line 1" required span={6} error={errors.addr1?.message} htmlFor="addr1">
                <Input id="addr1" {...form.register("addr1")} />
              </Field>
              <Field label="Address line 2" span={6} htmlFor="addr2">
                <Input id="addr2" {...form.register("addr2")} />
              </Field>
              <Field label="City" required span={4} error={errors.city?.message} htmlFor="city">
                <Input id="city" {...form.register("city")} />
              </Field>
              <Field label="State" required span={5} error={errors.state?.message} htmlFor="state">
                <NativeSelect id="state" {...form.register("state")}>
                  <option value="">Select state</option>
                  {INDIAN_STATES.map((s) => (
                    <option key={s.code} value={s.name}>
                      {s.name} ({s.code})
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Field label="PIN code" required span={3} error={errors.pincode?.message} htmlFor="pincode">
                <Input id="pincode" inputMode="numeric" maxLength={6} {...form.register("pincode")} />
              </Field>
            </FieldGrid>
          </FormSection>

          <FormSection
            id="statutory"
            title="Tax & statutory"
            description="Drives GST treatment on every invoice raised for this client"
            icon={<Landmark />}
          >
            <FieldGrid>
              <Field label="GST treatment" required span={4} htmlFor="gst_treatment">
                <NativeSelect id="gst_treatment" {...form.register("gst_treatment")}>
                  {GST_TREATMENTS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Field
                label="GSTIN"
                required={gstRequired}
                span={4}
                error={errors.gst_no?.message}
                hint={gstRequired ? undefined : "Optional for this treatment"}
                htmlFor="gst_no"
              >
                <Input
                  id="gst_no"
                  {...form.register("gst_no")}
                  maxLength={15}
                  className="font-mono uppercase"
                  placeholder="27AABCU9603R1ZM"
                />
              </Field>
              <Field label="Place of supply" span={4} hint="Defaults to the state above" htmlFor="place_of_supply">
                <NativeSelect id="place_of_supply" {...form.register("place_of_supply")}>
                  <option value="">Same as state</option>
                  {INDIAN_STATES.map((s) => (
                    <option key={s.code} value={s.name}>
                      {s.name} ({s.code})
                    </option>
                  ))}
                </NativeSelect>
              </Field>

              <Field label="PAN" span={3} error={errors.pan_no?.message} htmlFor="pan_no">
                <Input id="pan_no" {...form.register("pan_no")} maxLength={10} className="font-mono uppercase" />
              </Field>
              <Field label="CIN" span={4} htmlFor="cin_no">
                <Input id="cin_no" {...form.register("cin_no")} className="font-mono uppercase" />
              </Field>
              <Field label="MSME / Udyam no." span={5} htmlFor="msme_udyam_no">
                <Input id="msme_udyam_no" {...form.register("msme_udyam_no")} className="font-mono" />
              </Field>

              <ToggleField
                label="TDS applicable"
                description="Client deducts tax at source"
                span={6}
                checked={form.watch("tds_applicable")}
                onChange={(v) => form.setValue("tds_applicable", v, { shouldDirty: true })}
              />
              <ToggleField
                label="TCS applicable"
                description="Collect tax at source above threshold"
                span={6}
                checked={form.watch("tcs_applicable")}
                onChange={(v) => form.setValue("tcs_applicable", v, { shouldDirty: true })}
              />
            </FieldGrid>
          </FormSection>

          <FormSection
            id="commercial"
            title="Commercial terms"
            description="Applied as defaults on quotations and sales orders"
            icon={<Banknote />}
          >
            <FieldGrid>
              <Field label="Payment terms" required span={4} htmlFor="payment_terms">
                <NativeSelect id="payment_terms" {...form.register("payment_terms")}>
                  {PAYMENT_TERMS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Field
                label="Credit days"
                span={4}
                error={errors.credit_days?.message}
                hint={paymentTerms === "advance" ? "Not used for advance terms" : undefined}
                htmlFor="credit_days"
              >
                <Input id="credit_days" type="number" min={0} max={365} {...form.register("credit_days")} />
              </Field>
              <Field
                label="Credit limit"
                span={4}
                error={errors.credit_limit?.message}
                hint="0 means no limit enforced"
                htmlFor="credit_limit"
              >
                <Input id="credit_limit" type="number" min={0} step="1000" {...form.register("credit_limit")} />
              </Field>

              <Field
                label="Opening balance"
                span={4}
                error={errors.opening_balance?.message}
                hint="Brought forward AR so ledgers match"
                htmlFor="opening_balance"
              >
                <Input
                  id="opening_balance"
                  type="number"
                  step="1000"
                  {...form.register("opening_balance")}
                />
              </Field>
              <Field
                label="Opening as of"
                span={4}
                error={errors.opening_balance_as_of?.message}
                hint="Cut-off date for opening balance"
                htmlFor="opening_balance_as_of"
              >
                <Input id="opening_balance_as_of" type="date" {...form.register("opening_balance_as_of")} />
              </Field>

              <Field label="Price list" span={4} htmlFor="price_list">
                <NativeSelect id="price_list" {...form.register("price_list")}>
                  {PRICE_LISTS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Field label="Rate basis" span={4} htmlFor="rate_basis">
                <NativeSelect id="rate_basis" {...form.register("rate_basis")}>
                  {RATE_BASIS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Field label="Currency" span={4} error={errors.currency?.message} htmlFor="currency">
                <NativeSelect id="currency" {...form.register("currency")}>
                  <option value="INR">INR — Indian rupee</option>
                  <option value="USD">USD — US dollar</option>
                  <option value="EUR">EUR — Euro</option>
                  <option value="AED">AED — UAE dirham</option>
                </NativeSelect>
              </Field>
            </FieldGrid>
          </FormSection>

          <FormSection
            id="dispatch"
            title="Dispatch preferences"
            description="Pre-fills the delivery challan and loading slip"
            icon={<Truck />}
          >
            <FieldGrid>
              <Field label="Delivery terms" span={4} htmlFor="delivery_terms">
                <NativeSelect id="delivery_terms" {...form.register("delivery_terms")}>
                  {DELIVERY_TERMS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Field label="Freight terms" span={4} htmlFor="freight_terms">
                <NativeSelect id="freight_terms" {...form.register("freight_terms")}>
                  {FREIGHT_TERMS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Field label="Packing" span={4} htmlFor="packing_type">
                <NativeSelect id="packing_type" {...form.register("packing_type")}>
                  {PACKING_TYPES.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Field label="Preferred transporter" span={6} htmlFor="preferred_transporter">
                <Input id="preferred_transporter" {...form.register("preferred_transporter")} />
              </Field>
              <Field
                label="Dispatch instructions"
                span={6}
                hint="Gate timings, unloading, documents to carry"
                htmlFor="dispatch_instructions"
              >
                <Input id="dispatch_instructions" {...form.register("dispatch_instructions")} />
              </Field>
            </FieldGrid>
          </FormSection>

          <FormSection
            id="quality"
            title="Quality requirements"
            description="Enforced before dispatch clearance"
            icon={<ShieldCheck />}
          >
            <FieldGrid>
              <Field label="Governing standard" span={6} htmlFor="quality_standard">
                <Input id="quality_standard" list="quality-standards" {...form.register("quality_standard")} />
                <datalist id="quality-standards">
                  {QUALITY_STANDARDS.map((s) => (
                    <option key={s} value={s} />
                  ))}
                </datalist>
              </Field>
              <ToggleField
                label="Mill test certificate"
                description="Attach MTC with every dispatch"
                span={3}
                checked={form.watch("requires_test_certificate")}
                onChange={(v) => form.setValue("requires_test_certificate", v, { shouldDirty: true })}
              />
              <ToggleField
                label="Third-party inspection"
                description="Hold dispatch until inspection clears"
                span={3}
                checked={form.watch("requires_third_party_inspection")}
                onChange={(v) => form.setValue("requires_third_party_inspection", v, { shouldDirty: true })}
              />
              <Field label="Tolerance / spec notes" span={12} htmlFor="tolerance_notes">
                <Textarea id="tolerance_notes" rows={2} {...form.register("tolerance_notes")} />
              </Field>
              <Field label="Internal notes" span={12} hint="Visible to staff only" htmlFor="notes">
                <Textarea id="notes" rows={2} {...form.register("notes")} />
              </Field>
            </FieldGrid>
          </FormSection>
        </form>
      </div>

      {isDirty ? (
        <div className="pointer-events-none fixed inset-x-0 bottom-0 z-30 flex justify-center pb-4">
          <div className="pointer-events-auto flex items-center gap-3 rounded-full border border-border-strong bg-popover px-3 py-1.5 shadow-lg">
            <span className="text-xs text-muted-foreground">Unsaved changes</span>
            <Button type="button" variant="ghost" size="xs" onClick={() => form.reset()}>
              Discard
            </Button>
            <Button type="submit" form="client-form" size="xs" disabled={busy}>
              Save
              <kbd className="ml-1 rounded bg-primary-foreground/20 px-1 text-[10px]">⌘S</kbd>
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function countErrors(errors: FieldErrors<ClientFormValues>) {
  return Object.keys(errors).length;
}
